import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ModeloDocumentoService } from '@shared/services/apis/dfe/modelo-documento.service';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { ReformaTributoRegraSectionComponent } from './sections/tributo-regra-section/tributo-regra-section.component';
import { ReformaTributoRegrasRelacionadasSectionComponent } from './sections/tributo-regras-relacionadas-section/tributo-regras-relacionadas-section.component';
import { RegraTributariaSelectDialogComponent, RegraTributariaSelectDialogData } from './regra-tributaria-select-dialog/regra-tributaria-select-dialog.component';
import { RegraTributariaNaturezaOperacaoStruct } from '@shared/services/structs/motor-fiscal/regra-tributaria-natureza-operacao.struct';

/**
 * Aba Reforma Tributária (IBS/CBS/IS).
 */
@Component({
    selector: 'app-natureza-operacao-reforma-tributaria-tab',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AtivacaoSectionCardComponent,
        AlertCardComponent,
        ReformaTributoRegraSectionComponent,
        ReformaTributoRegrasRelacionadasSectionComponent,
    ],
    templateUrl: './natureza-operacao-reforma-tributaria-tab.component.html',
    styleUrl: './natureza-operacao-reforma-tributaria-tab.component.css',
})
export class NaturezaOperacaoReformaTributariaTabComponent implements OnInit {
    @Input({ required: true }) form!: FormGroup;

    // Modelo de documento pertence ao db_dfe: as regras trazem apenas o codigo; mapeamos codigo -> nome.
    public nomeModeloPorCodigo: Record<string, string> = {};

    constructor(
        private dialog: MatDialog,
        private modeloDocumentoService: ModeloDocumentoService,
    ) {}

    ngOnInit(): void {
        this.modeloDocumentoService.listar().subscribe({
            next: res => {
                this.nomeModeloPorCodigo = {};
                for (const m of (res.modelos || [])) {
                    if (m.codigo) {
                        this.nomeModeloPorCodigo[m.codigo] = m.nome || m.descricao || m.codigo;
                    }
                }
            },
            error: () => { /* fallback: exibe o codigo */ },
        });
    }

    public get reformaForm(): FormGroup {
        return this.form.get('reformaTributaria') as FormGroup;
    }

    public get isAtivoControl(): FormControl {
        return this.reformaForm.get('isAtivo') as FormControl;
    }

    public get regraPadrao(): RegraTributariaNaturezaOperacaoStruct | null {
        return this.reformaForm.get('_regraTributariaPadrao')?.value ?? null;
    }

    /**
     * Regra padrão obrigatória e ainda não vinculada. O vínculo não é um campo comum de
     * formulário, então o erro é exibido dentro da própria seção de Regra Padrão.
     * Sem checar touched: não existe interação de digitação/blur que marque o control, então
     * o aviso precisa aparecer assim que a aba é ativada sem regra - e não só ao salvar.
     */
    public get regraPadraoPendente(): boolean {
        return !!this.reformaForm.get('idRegraTributariaPadrao')?.hasError('required');
    }

    public get regrasRelacionadas(): RegraTributariaNaturezaOperacaoStruct[] {
        return this.reformaForm.get('regrasRelacionadas')?.value ?? [];
    }

    public abrirSelecionarRegraPadrao(): void {
        const data: RegraTributariaSelectDialogData = {
            titulo: 'Selecionar Regra Padrão',
            multiple: false,
            selecionadas: this.regraPadrao ? [this.regraPadrao] : [],
        };

        const ref = this.dialog.open(RegraTributariaSelectDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });

        ref.afterClosed().subscribe((selecionadas: RegraTributariaNaturezaOperacaoStruct[] | undefined) => {
            const regra = selecionadas?.[0] ?? null;
            if (!regra) return;

            this.reformaForm.get('idRegraTributariaPadrao')?.setValue(regra.idRegraTributaria);
            this.reformaForm.get('_regraTributariaPadrao')?.setValue(regra);
            this.reformaForm.get('regrasRelacionadas')?.setValue(
                this.regrasRelacionadas.filter(item => item.idRegraTributaria !== regra.idRegraTributaria),
            );
        });
    }

    public abrirRelacionarRegras(): void {
        const data: RegraTributariaSelectDialogData = {
            titulo: 'Relacionar Regras Tributárias',
            multiple: true,
            selecionadas: this.regrasRelacionadas,
            regraPadraoId: this.regraPadrao?.idRegraTributaria,
        };

        const ref = this.dialog.open(RegraTributariaSelectDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });

        ref.afterClosed().subscribe((selecionadas: RegraTributariaNaturezaOperacaoStruct[] | undefined) => {
            if (!selecionadas) return;

            const idPadrao = this.regraPadrao?.idRegraTributaria ?? null;
            this.reformaForm.get('regrasRelacionadas')?.setValue(
                selecionadas.filter(item => item.idRegraTributaria !== idPadrao),
            );
        });
    }

    public cadastrarNovaRegra(): void {
        window.open('/configuracoes/reforma-tributaria/regras/cadastrar', '_blank');
    }

    public editarRegra(regra: RegraTributariaNaturezaOperacaoStruct): void {
        window.open(`/configuracoes/reforma-tributaria/regras/cadastrar/${regra.idRegraTributaria}`, '_blank');
    }

    public removerRegraPadrao(): void {
        this.reformaForm.get('idRegraTributariaPadrao')?.setValue(null);
        this.reformaForm.get('_regraTributariaPadrao')?.setValue(null);
    }

    public removerRegraRelacionada(regra: RegraTributariaNaturezaOperacaoStruct): void {
        this.reformaForm.get('regrasRelacionadas')?.setValue(
            this.regrasRelacionadas.filter(item => item.idRegraTributaria !== regra.idRegraTributaria),
        );
    }
}
